import React, { useEffect, useState } from 'react';
import { navigateSphereWithFilters } from '@common/enums/query-params/filters';
import { flattenParents } from 'models/organization/parent-organization';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  selectCurrentOrganizationId,
  selectOrganizationsData,
} from 'store/organization/organization-slice-selectors';
import { selectRobotsData } from 'store/robots/robots-slice-selectors';
import {
  AddIcon,
  DownArrow,
  LocationsIcon,
  OpenIcon,
  OpenInNewTabIcon,
} from '@common/utils/icons/icons';
import { StringAvatar } from 'components/data-widgets/StringAvatar';
import { selectIsUserAdminOrHigher, selectUser } from 'store/general/general-slice-selectors';
import { setSelectedAgreementId } from 'store/general/general-slice';
import OrganizationsForm from 'components/organizations/OrganizationForm';
import OrganizationRole from 'models/enums/organization-roles';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getLocationSetById } from 'store/organization/organization-actions';
import NavigationButton from './NavigationButton';
import UserRole from 'models/enums/user-roles';
import { useTranslation } from 'react-i18next';

const OrganizationBreadcrumbs: React.FC = () => {
  const { t } = useTranslation();

  const theme = useTheme();
  const matchesSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));
  const organizationsData = useAppSelector((state) => selectOrganizationsData(state));
  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const userData = useAppSelector((state) => selectUser(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isOrganizationFormOpen, setIsOrganizationFormOpen] = useState(false);

  useEffect(() => {
    if (
      currentOrganizationId != null &&
      organizationsData.rest.getLocationSetStatus === HttpFetchStatus.Idle
    ) {
      dispatch(getLocationSetById(currentOrganizationId));
    }
  }, [currentOrganizationId, dispatch, organizationsData.rest.getLocationSetStatus]);

  const navigateBreadcrumbClickHandler = (id: string) => {
    navigate(navigateSphereWithFilters(id, true));
  };

  const openSelectOrgPopoverClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeSelectOrgPopoverHandler = () => {
    setAnchorEl(null);
  };

  const openOrganizationFormClickHandler = () => {
    setIsOrganizationFormOpen(true);
  };

  const closeOrganizationFormHandler = () => {
    setIsOrganizationFormOpen(false);
  };

  const navigateClickHandler = (id: string) => {
    navigate(navigateSphereWithFilters(id, true));
    dispatch(setSelectedAgreementId(null));
    setAnchorEl(null);
  };

  const newTabClickHandler = (id: string) => {
    const url = navigateSphereWithFilters(id, true);
    window.open(url.toString(), '_blank');
  };

  const locationParent =
    organizationsData.locationSet != null
      ? organizationsData.locationSet.length === 1
        ? organizationsData.locationSet[0]
        : organizationsData.locationSet.find((org) => org.role !== OrganizationRole.LOCATION)
      : null;
  const filteredLocations =
    organizationsData.locationSet?.filter(
      (org) => org.role === OrganizationRole.LOCATION && org.parentId === locationParent?.id,
    ) ?? [];
  const locationLimitReached =
    organizationsData.current?.locationLimit != null &&
    filteredLocations.length >= organizationsData.current!.locationLimit!;

  const parents =
    organizationsData.current?.parent != null
      ? flattenParents(organizationsData.current.parent).reverse()
      : [];

  const canAddLocation =
    isAdmin &&
    locationParent != null &&
    locationParent.role !== OrganizationRole.LOCATION &&
    !(
      userData.data.role === UserRole.MODERATOR &&
      organizationsData.current?.role === OrganizationRole.LOCATION
    );

  // Nothing to switch to and nothing to create: the dropdown would open an empty
  // popover, so render the organization as plain, non-interactive text instead.
  const isSelectionRedundant =
    filteredLocations.length <= 1 && !canAddLocation && !(matchesSmDown && parents.length > 0);

  const organizationSubtitle = [
    organizationsData.current?.role != null
      ? t('organization-role-' + organizationsData.current.role)
      : null,
    robotsData.pagination?.count != null
      ? `${robotsData.pagination.count} ${t('table-header-robot-count').toLocaleLowerCase()}`
      : null,
  ]
    .filter((part) => part != null)
    .join(' · ');

  return (
    <React.Fragment>
      {matchesSmDown ? (
        isSelectionRedundant ? (
          <Stack justifyContent="center" px={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="h3" fontSize={16} textTransform="none" noWrap sx={{ minWidth: 0 }}>
              {organizationsData.current?.name}
            </Typography>
            {organizationSubtitle.length > 0 && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {organizationSubtitle}
              </Typography>
            )}
          </Stack>
        ) : (
          <ButtonBase
            onClick={openSelectOrgPopoverClickHandler}
            sx={{
              px: 1,
              borderRadius: 1,
              alignItems: 'flex-start',
              flexDirection: 'column',
              width: '100%',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
              <Typography variant="h3" fontSize={16} textTransform="none" noWrap sx={{ minWidth: 0 }}>
                {organizationsData.current?.name}
              </Typography>
              <DownArrow fontSize="small" />
            </Stack>
            {organizationSubtitle.length > 0 && (
              <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
                {organizationSubtitle}
              </Typography>
            )}
          </ButtonBase>
        )
      ) : (
        <Breadcrumbs
          maxItems={matchesSmDown ? 1 : 2}
          itemsBeforeCollapse={matchesSmDown ? 0 : 1}
          aria-label="breadcrumb"
          sx={{
            '.MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
          }}
        >
          {parents.map((parent) => (
            <NavigationButton
              isNavOpen
              title={parent.name}
              key={parent.id}
              onClick={() => navigateBreadcrumbClickHandler(parent.id)}
              contextMenuOptions={[
                {
                  title: t('button-open-in-new-tab'),
                  icon: <OpenInNewTabIcon color="action" />,
                  action: () => newTabClickHandler(parent.id),
                },
              ]}
              icon={undefined}
            />
          ))}
          {isSelectionRedundant ? (
            <Typography
              variant="h3"
              fontSize={16}
              textTransform="none"
              color="GrayText"
              whiteSpace="nowrap"
              paddingX={1}
            >
              {organizationsData.current?.name}
            </Typography>
          ) : (
            <Button endIcon={<DownArrow />} onClick={openSelectOrgPopoverClickHandler}>
              <Typography
                variant="h3"
                fontSize={16}
                textTransform="none"
                color="GrayText"
                whiteSpace="nowrap"
              >
                {organizationsData.current?.name}
              </Typography>
            </Button>
          )}
        </Breadcrumbs>
      )}
      <Popover
        id="user-popover-id"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeSelectOrgPopoverHandler}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack sx={{ minWidth: theme.spacing(32) }}>
          {matchesSmDown &&
            parents.map((parent) => (
              <NavigationButton
                isNavOpen
                icon={<OpenIcon />}
                title={parent.name}
                key={parent.id}
                onClick={() => {
                  closeSelectOrgPopoverHandler();
                  navigateBreadcrumbClickHandler(parent.id);
                }}
                contextMenuOptions={[
                  {
                    title: t('button-open-in-new-tab'),
                    icon: <OpenInNewTabIcon color="action" />,
                    action: () => newTabClickHandler(parent.id),
                  },
                ]}
              />
            ))}

          <Typography variant="h3" padding={2} pb={1}>
            {t('title-locations')}
          </Typography>
          {filteredLocations.map((org) => (
            <NavigationButton
              key={org.id}
              isNavOpen={true}
              title={
                <Box display="block" pl={2.5} width="100%" overflow="hidden">
                  <Stack direction="row" alignItems="center" width="100%" spacing={2}>
                    <Typography
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      width="100%"
                      overflow="hidden"
                      textAlign="start"
                      flexGrow={1}
                    >
                      {org.name}
                    </Typography>

                    <Typography whiteSpace="nowrap">{org.robotCount}</Typography>
                  </Stack>
                </Box>
              }
              icon={
                filteredLocations.length > 1 ? (
                  <StringAvatar
                    color={
                      currentOrganizationId === org.id ? theme.palette.primary.main : undefined
                    }
                    sx={{
                      ml: '-2px',
                    }}
                    name={org.name}
                  />
                ) : (
                  <LocationsIcon />
                )
              }
              isCurrentPage={currentOrganizationId === org.id}
              onClick={() => navigateClickHandler(org.id)}
              contextMenuOptions={[
                {
                  title: t('button-open-in-new-tab'),
                  icon: <OpenInNewTabIcon color="action" />,
                  action: () => newTabClickHandler(org.id),
                },
              ]}
            />
          ))}

          {canAddLocation && (
            <NavigationButton
              isNavOpen={true}
              disabled={{
                disabled: locationLimitReached,
                tooltip: t('info-tooltip-limit-reached'),
              }}
              lowerCase
              title={
                <Typography
                  sx={{
                    transition: 'all 0.4s ease-in-out',
                    ml: 2.5,
                    whiteSpace: 'nowrap',
                    color: locationLimitReached ? undefined : theme.palette.primary.main,
                  }}
                >
                  {t('button-add-LOCATION')}
                </Typography>
              }
              icon={<AddIcon color={locationLimitReached ? 'disabled' : 'primary'} />}
              onClick={openOrganizationFormClickHandler}
            />
          )}
        </Stack>
      </Popover>
      {locationParent != null && (
        <OrganizationsForm
          organization={null}
          parentId={locationParent?.id}
          open={isOrganizationFormOpen}
          role={OrganizationRole.LOCATION}
          onClose={closeOrganizationFormHandler}
        />
      )}
    </React.Fragment>
  );
};

export default OrganizationBreadcrumbs;
